import type {
  CreateUnsettlementsActionRequest,
  FormattedProjectionScoreData,
  UnsettlementFormUpdateData
} from '@library/types/unsettlements';
import {
  ProjectionSearch,
  ReasonCodeSelect,
  ScoringInputs,
  WinProtectionToggle
} from '@bo-components/unsettlements';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger
} from '@library/components/buttons';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { CheckboxOnChangeArg } from '@library/types/utils';
import type { ComboboxOption } from '@library/components/selects/combobox/Combobox';

export interface UnsettlementsCreateFormProps {
  activeTab: 'resettlement' | 'unsettlement';
  errorMessage?: string;
  handleOnSetUnsettlementData: (data: UnsettlementFormUpdateData) => void;
  isError: boolean;
  isProjectionDataLoading: boolean;
  isProjectionValid: boolean;
  newUnsettlementData: CreateUnsettlementsActionRequest;
  originalProjectionDetails?: FormattedProjectionScoreData;
  setActiveTab: (tab: 'resettlement' | 'unsettlement') => void;
}

export const UnsettlementsCreateForm = ({
  activeTab,
  errorMessage = 'There was an issue with the projection.',
  handleOnSetUnsettlementData,
  isError,
  isProjectionDataLoading,
  isProjectionValid,
  newUnsettlementData,
  originalProjectionDetails = undefined,
  setActiveTab
}: UnsettlementsCreateFormProps) => {
  // INPUT STATES
  // states need to be reset based on the active tab and prop changes from newUnsettlementData
  // needs the initial value from newUnsettlementData/originalProjectionDetails
  const [isDnp, setIsDnp] = useState<boolean | undefined>(
    newUnsettlementData.updated_score_attributes?.is_off_the_board ??
      originalProjectionDetails?.isDnp
  );

  const [isReboot, setIsReboot] = useState<boolean | undefined>(
    newUnsettlementData.updated_score_attributes?.unders_win_dnp ??
      originalProjectionDetails?.isReboot
  );
  const [isWinProtected, setIsWinProtected] = useState<boolean | undefined>(
    newUnsettlementData.is_win_protected
  );
  const [score, setScore] = useState<number | string | undefined>(
    newUnsettlementData.updated_score_attributes?.score ??
      originalProjectionDetails?.score
  );

  useEffect(() => {
    // If activeTab === 'unsettlement' - default to original values
    if (activeTab === 'unsettlement') {
      setIsWinProtected(false);
      setIsDnp(originalProjectionDetails?.isDnp);
      setIsReboot(originalProjectionDetails?.isReboot);
      setScore(originalProjectionDetails?.score);
    } else {
      // activeTab === 'resettlement'
      setIsWinProtected(newUnsettlementData.is_win_protected);
      setIsDnp(
        newUnsettlementData.updated_score_attributes?.is_off_the_board ??
          originalProjectionDetails?.isDnp
      );
      setIsReboot(
        newUnsettlementData.updated_score_attributes?.unders_win_dnp ??
          originalProjectionDetails?.isReboot
      );
      setScore(
        newUnsettlementData.updated_score_attributes?.score ??
          originalProjectionDetails?.score
      );
    }
  }, [newUnsettlementData, originalProjectionDetails, activeTab]);

  // CONSTANTS
  const unsettlementReason =
    newUnsettlementData.unsettlement_reason ?? undefined;
  const projectionId = newUnsettlementData.projection_id ?? undefined;

  const reasonInputValue = useMemo(
    () =>
      unsettlementReason
        ? { label: unsettlementReason, value: unsettlementReason }
        : null,
    [unsettlementReason]
  );

  // HELPER FUNCTIONS
  const triggerUnsettlementDataUpdate = useCallback(
    (updates: Partial<UnsettlementFormUpdateData>) => {
      // merge current state with provided updates
      const updatedData: UnsettlementFormUpdateData = {
        isDnp: updates.isDnp ?? isDnp,
        isReboot: updates.isReboot ?? isReboot,
        isWinProtected: updates.isWinProtected ?? isWinProtected,
        projectionIdValue: updates.projectionIdValue ?? projectionId,
        reasonValue: updates.reasonValue ?? reasonInputValue,
        score: updates.score !== undefined ? updates.score : score
      };
      handleOnSetUnsettlementData(updatedData);
    },
    [
      handleOnSetUnsettlementData,
      isDnp,
      isReboot,
      isWinProtected,
      projectionId,
      reasonInputValue,
      score
    ]
  );

  const handleOnChangeProjectionId = useCallback(
    (value: string | number | undefined) => {
      triggerUnsettlementDataUpdate({ projectionIdValue: value });
    },
    [triggerUnsettlementDataUpdate]
  );

  const handleOnChangeReasonCode = useCallback(
    (selectedOption: ComboboxOption<string | number> | null) => {
      triggerUnsettlementDataUpdate({ reasonValue: selectedOption });
    },
    [triggerUnsettlementDataUpdate]
  );

  const handleOnToggleDnp = useCallback(
    ({ checked }: CheckboxOnChangeArg) => {
      setIsDnp(checked);
      triggerUnsettlementDataUpdate({ isDnp: checked });
    },
    [triggerUnsettlementDataUpdate]
  );

  const handleOnToggleReboot = useCallback(
    ({ checked }: CheckboxOnChangeArg) => {
      setIsReboot(checked);
      triggerUnsettlementDataUpdate({ isReboot: checked });
    },
    [triggerUnsettlementDataUpdate]
  );

  const handleOnToggleWinProtection = useCallback(
    (checked: boolean) => {
      setIsWinProtected(checked);
      triggerUnsettlementDataUpdate({ isWinProtected: checked });
    },
    [triggerUnsettlementDataUpdate]
  );

  const handleOnChangeScore = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;
      setScore(value);
      triggerUnsettlementDataUpdate({ score: value });
    },
    [triggerUnsettlementDataUpdate]
  );

  const { playerImgUrl, playerName, projectedScore, teamDetails } =
    originalProjectionDetails || {};

  return (
    <div className="flex w-[1100px] justify-center">
      {/* ADD NEW UNSETTLEMENT SECTION */}
      <div className="flex w-[550px] flex-col gap-y-6 pr-6">
        {/* Header */}
        <div>
          <p className="text-soClean-100 mb-1 text-sm font-medium">
            Add new unsettlement
          </p>
          <p className="text-soClean-180 text-xs">
            Configure all unsettlement properties below.
          </p>
        </div>
        {/* Error */}
        {isError && (
          <p className="text-status-failure text-xs">{errorMessage}</p>
        )}
        <ProjectionSearch
          isProjectionDataLoading={isProjectionDataLoading}
          isProjectionValid={isProjectionValid}
          onValueChange={handleOnChangeProjectionId}
          playerImgUrl={playerImgUrl}
          playerName={playerName}
          projectedScore={projectedScore}
          projectionId={projectionId}
          teamDetails={teamDetails}
        />
        <ReasonCodeSelect
          onChange={handleOnChangeReasonCode}
          unsettlementReason={reasonInputValue}
        />
      </div>

      {/* ACTION DETAILS SECTION */}
      <div className="border-soFresh-130 flex w-[550px] flex-col border-l pl-6">
        {/* Header */}
        <div>
          <p className="text-soClean-100 mb-1 text-sm font-medium">
            Action details
          </p>
          <p className="text-soClean-180 text-xs">
            This option will only perform the unsettlement action without
            changing scoring attributes.
          </p>
          {/* Win protected toggle */}
          <Tabs
            className="mt-6"
            defaultValue={activeTab}
            onValueChange={setActiveTab as (value: string) => void}
          >
            <TabsList>
              <TabsTrigger value="resettlement">Resettlement</TabsTrigger>
              <TabsTrigger value="unsettlement">Unsettlement</TabsTrigger>
            </TabsList>
            {/* RESETTLEMENT TAB */}
            <TabsContent value="resettlement">
              <WinProtectionToggle
                isDisabled={!isProjectionValid}
                isWinProtected={isWinProtected}
                onToggle={handleOnToggleWinProtection}
              />
              <ScoringInputs
                isDisabled={!isProjectionValid}
                isDnp={isDnp}
                isReboot={isReboot}
                score={score}
                onToggleDnp={handleOnToggleDnp}
                onToggleReboot={handleOnToggleReboot}
                onChangeScore={handleOnChangeScore}
              />
            </TabsContent>
            {/* UNSETTLEMENT TAB - always disabled */}
            <TabsContent value="unsettlement">
              <WinProtectionToggle
                isDisabled
                isWinProtected={false}
                onToggle={handleOnToggleWinProtection}
              />
              <ScoringInputs
                isDisabled
                isDnp={originalProjectionDetails?.isDnp}
                isReboot={originalProjectionDetails?.isReboot}
                score={originalProjectionDetails?.score}
                onToggleDnp={handleOnToggleDnp}
                onToggleReboot={handleOnToggleReboot}
                onChangeScore={handleOnChangeScore}
              />
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
};
